const validNumber = (value, minimum = 0, maximum = null) => {
    if (
        value === null
        || value === ''
        || (typeof value !== 'number' && typeof value !== 'string')
    ) {
        return null;
    }

    const number = Number(value);
    if (
        !Number.isFinite(number)
        || number < minimum
        || (maximum !== null && number > maximum)
    ) {
        return null;
    }

    return number;
};

const matchesRange = (value, range) => {
    const minInclusive = validNumber(range?.minInclusive);
    const minExclusive = validNumber(range?.minExclusive);
    const maxInclusive = validNumber(range?.maxInclusive);
    const maxExclusive = validNumber(range?.maxExclusive);

    return (minInclusive === null || value >= minInclusive)
        && (minExclusive === null || value > minExclusive)
        && (maxInclusive === null || value <= maxInclusive)
        && (maxExclusive === null || value < maxExclusive);
};

const getRating = (rawValue, ranges, maximum = null) => {
    const value = validNumber(rawValue, 0, maximum);
    if (value === null || !Array.isArray(ranges)) {
        return null;
    }

    const range = ranges.find((candidate) => matchesRange(value, candidate));
    const level = validNumber(range?.level, 1, 5);
    if (!range || level === null || typeof range.label !== 'string' || !range.label.trim()) {
        return null;
    }

    return {
        level,
        label: range.label.trim(),
        rawValue: value,
    };
};

export const getDifficultyRating = (profile, ratingConfig) => (
    getRating(profile, ratingConfig?.difficulty, 100)
);

export const getDurationRating = (averageTurns, ratingConfig) => (
    getRating(averageTurns, ratingConfig?.duration)
);

export const hasIncreasedUncertainty = (solveRate, ratingConfig) => {
    const rate = validNumber(solveRate, 0, 1);
    const threshold = validNumber(ratingConfig?.uncertaintySolveRate, 0, 1);

    return rate !== null && threshold !== null && rate < threshold;
};

export const selectRatingMetrics = (difficulty, players) => {
    const safeDifficulty = difficulty && typeof difficulty === 'object' ? difficulty : {};
    const profiles = safeDifficulty.by_player_count;
    const playerProfile = profiles && typeof profiles === 'object'
        ? profiles[String(players)]
        : null;
    const hasPlayerProfile = playerProfile && typeof playerProfile === 'object';
    const source = hasPlayerProfile ? playerProfile : safeDifficulty;

    return {
        index: validNumber(safeDifficulty.overall_index_rounds),
        profile: validNumber(
            source[hasPlayerProfile
                ? 'empirical_difficulty_profile_0_to_100'
                : 'overall_tip_profile_0_to_100'],
            0,
            100
        ),
        averageTurns: validNumber(source.average_turns),
        solveRate: validNumber(source.solve_rate ?? safeDifficulty.solve_rate, 0, 1),
        runs: validNumber(source.runs ?? safeDifficulty.runs),
        source: hasPlayerProfile ? 'player_count' : 'overall',
    };
};

const ratingLabelForLevel = (ranges, level) => {
    const range = Array.isArray(ranges)
        ? ranges.find((candidate) => Number(candidate?.level) === level)
        : null;

    return typeof range?.label === 'string' && range.label.trim()
        ? range.label.trim()
        : `${level} von 5`;
};

const normalizeMetric = (items, valueForItem, ranges, absoluteRating) => {
    const values = items
        .map((item) => ({
            item,
            value: validNumber(valueForItem(item)),
        }))
        .filter(({ value }) => value !== null);

    if (values.length <= 1) {
        return new Map(values.map(({ item, value }) => [item, absoluteRating(value)]));
    }

    const sorted = [...values].sort((first, second) => first.value - second.value);
    const ratings = new Map();

    sorted.forEach(({ item, value }, index) => {
        const tiedIndexes = sorted
            .map((candidate, candidateIndex) => (
                candidate.value === value ? candidateIndex : null
            ))
            .filter((candidateIndex) => candidateIndex !== null);
        const averageIndex = tiedIndexes.reduce((sum, tiedIndex) => sum + tiedIndex, 0)
            / tiedIndexes.length;
        const level = Math.round(1 + ((averageIndex / (sorted.length - 1)) * 4));

        ratings.set(item, {
            level,
            label: ratingLabelForLevel(ranges, level),
            rawValue: value,
            normalized: true,
        });
    });

    return ratings;
};

export const normalizePuzzleRatings = (puzzles, players, ratingConfig) => {
    const items = Array.isArray(puzzles) ? puzzles : [];
    const metricsForPuzzle = (puzzle) => {
        const fallback = selectRatingMetrics(puzzle?.difficulty, players);
        const stored = puzzle?.ratingMetrics?.[String(players)];

        return stored && typeof stored === 'object'
            ? {
                ...fallback,
                ...stored,
                index: validNumber(stored.index) ?? fallback.index,
            }
            : fallback;
    };
    const difficultyRatings = normalizeMetric(
        items,
        (puzzle) => metricsForPuzzle(puzzle)?.index,
        ratingConfig?.difficulty,
        (value) => getDifficultyRating(value, ratingConfig)
    );
    const durationRatings = normalizeMetric(
        items,
        (puzzle) => metricsForPuzzle(puzzle)?.averageTurns,
        ratingConfig?.duration,
        (value) => getDurationRating(value, ratingConfig)
    );

    return new Map(items.map((puzzle) => [
        puzzle,
        {
            difficulty: difficultyRatings.get(puzzle) || null,
            duration: durationRatings.get(puzzle) || null,
        },
    ]));
};

export const sortPuzzlesByDifficulty = (puzzles, players) => {
    if (!Array.isArray(puzzles)) {
        return [];
    }

    const difficultyValue = (puzzle) => {
        const stored = puzzle?.ratingMetrics?.[String(players)];
        const metrics = stored && typeof stored === 'object'
            ? stored
            : selectRatingMetrics(puzzle?.difficulty, players);

        return validNumber(metrics?.index)
            ?? validNumber(puzzle?.difficulty?.overall_index_rounds);
    };

    return [...puzzles].sort((first, second) => {
        const firstDifficulty = difficultyValue(first);
        const secondDifficulty = difficultyValue(second);

        if (firstDifficulty === null && secondDifficulty !== null) {
            return 1;
        }
        if (firstDifficulty !== null && secondDifficulty === null) {
            return -1;
        }
        if (firstDifficulty !== secondDifficulty) {
            return firstDifficulty - secondDifficulty;
        }

        const firstTitle = String(first?.title || first?.puzzleId || '');
        const secondTitle = String(second?.title || second?.puzzleId || '');
        return firstTitle.localeCompare(secondTitle, 'de', { sensitivity: 'base' });
    });
};
